import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as BundlesService from '~/services/BundlesService';
import * as TourOffersService from '~/services/TourOffersService';

import {
  Bundle,
  CreateBundleComponentPayload,
  ListBundlesQuery,
  UpdateBundlePayload,
  UpdateBundlePricesPayload,
} from '~/types/bundles/bundle';

import { tourOfferQueryKeys } from '../tours/useTourOffers';

export const bundleQueryKeys = {
  all: ['bundles'] as const,
  lists: () => [...bundleQueryKeys.all, 'list'] as const,
  list: (query: ListBundlesQuery) => [...bundleQueryKeys.lists(), query] as const,
  details: () => [...bundleQueryKeys.all, 'detail'] as const,
  detail: (bundleId: string | undefined) => [...bundleQueryKeys.details(), bundleId ?? ''] as const,
};

export function useGetBundles(query: ListBundlesQuery) {
  return useQuery({
    queryKey: bundleQueryKeys.list(query),
    queryFn: ({ signal }) => BundlesService.listBundles(query, { signal }),
    placeholderData: keepPreviousData,
  });
}

export function useGetBundle(bundleId: string | undefined) {
  return useQuery({
    queryKey: bundleQueryKeys.detail(bundleId),
    queryFn: ({ signal }) => BundlesService.getBundle(bundleId, { signal }),
    enabled: !!bundleId,
  });
}

export function useCreateBundle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => BundlesService.createBundle(name),
    onSuccess: (bundle) => {
      queryClient.setQueryData(bundleQueryKeys.detail(bundle.id), bundle);
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.lists() });
    },
  });
}

/** Every bundle write returns the whole bundle, so the detail cache is replaced rather than refetched. */
function useBundleMutation<TVariables>(bundleId: string, mutationFn: (variables: TVariables) => Promise<Bundle>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (bundle) => {
      queryClient.setQueryData(bundleQueryKeys.detail(bundleId), bundle);
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.lists() });
    },
  });
}

export function useUpdateBundle(bundleId: string) {
  return useBundleMutation(bundleId, (payload: UpdateBundlePayload) => BundlesService.updateBundle(bundleId, payload));
}

export function useCreateBundleComponent(bundleId: string) {
  return useBundleMutation(bundleId, (payload: CreateBundleComponentPayload) =>
    BundlesService.createBundleComponent(bundleId, payload),
  );
}

export function useDeleteBundleComponent(bundleId: string) {
  return useBundleMutation(bundleId, (componentId: string) =>
    BundlesService.deleteBundleComponent(bundleId, componentId),
  );
}

export function useSetBundleDepartureDates(bundleId: string) {
  return useBundleMutation(bundleId, (dates: Array<string>) => BundlesService.setBundleDepartureDates(bundleId, dates));
}

export function useUpdateBundlePrices(bundleId: string) {
  return useBundleMutation(bundleId, (payload: UpdateBundlePricesPayload) =>
    BundlesService.updateBundlePrices(bundleId, payload),
  );
}

/**
 * A draft's tour component points at its base tour. Approving makes the LTLE from that base in
 * svc-tour, points the component at it, then approves. A component already on an LTLE is
 * skipped, so approving again after a failure carries on from where it stopped.
 */
export function useApproveBundle(bundleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (bundle: Bundle) => {
      for (const component of bundle.components) {
        if (component.product_type !== 'tour') continue;

        const offers = await TourOffersService.searchTourOffers({ search: component.tour_offer_id });
        const offer = offers.find((o) => o.id === component.tour_offer_id);
        if (!offer) {
          throw new Error(`Tour offer ${component.tour_offer_id} was not found`);
        }
        if (offer.isLtle) continue;

        const ltle = await TourOffersService.createLtle(component.tour_offer_id, { name: bundle.name });
        await BundlesService.setBundleComponentTourOffer(bundleId, component.id, { tour_offer_id: ltle.id });
      }
      return BundlesService.updateBundle(bundleId, { status: 'approved' });
    },
    onSuccess: (bundle) => {
      queryClient.setQueryData(bundleQueryKeys.detail(bundleId), bundle);
    },
    // A failure part way may still have moved a component onto its LTLE.
    onError: () => {
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.detail(bundleId) });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tourOfferQueryKeys.all });
    },
  });
}
